/**
 * <nd-bundle-picker> – themed bundle picker that mirrors and controls
 * the hidden Kaching Bundles widget. Kaching stays in the DOM to handle
 * cart logic, A/B-tested pricing, and discount application.
 */
(() => {
  const KACHING_SELECTORS = [
    'kaching-bundles-block',
    'kaching-bundle',
    'kaching-bundle-deals',
    '.kaching-bundles-form',
    '[class*="kaching-bundle"]',
    '[id*="kaching"]',
    '[data-kaching]',
  ];
  const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'LINK', 'META', 'NOSCRIPT']);
  const MAX_OFFERS = 8;
  const TIMEOUT_MS = 20000;
  const SEP = ' | ';
  const TITLE_RE = /(?:Köp|Køb|Kjøp)\s+\d+\s*,?\s*Få\s+\d+(?:\s+Gratis)?/i;
  const TITLE_RE_G = /(?:Köp|Køb|Kjøp)\s+\d+\s*,?\s*Få\s+\d+/gi;
  const PRICE_RE_G = /\d[\d\s.,]*\s*kr\b/gi;
  const PRICE_TEST = /\d\s*kr\b/i;
  const BADGE_RE = /mest\s+popul|maximal\s+besparing|bäst\s+värde|populär|største\s+besparelse|maksimal\s+besparelse/i;
  const PENDING_CLASS = 'nd-bp-pending';
  let pendingCount = 0;

  const DEBUG = location.search.includes('debug-bundles');
  // ?debug-bundles=show leaves Kaching's own widget visible under the picker for inspection.
  const SHOW_SOURCE = new URLSearchParams(location.search).get('debug-bundles') === 'show';
  const VERSION = '2026-10-20.2';
  window.ndBundlePickerVersion = VERSION;
  const log = DEBUG ? (...a) => console.log('[nd-bp]', ...a) : () => {};

  class NdBundlePicker extends HTMLElement {
    connectedCallback() {
      pendingCount++;
      this._pending = true;
      if (!SHOW_SOURCE) document.documentElement.classList.add(PENDING_CLASS);
      log('version', VERSION, SHOW_SOURCE ? '(Kaching widget left visible)' : '');
      this._loadProductData();
      this._waitForKaching();
    }

    disconnectedCallback() {
      this._bodyMo?.disconnect();
      this._syncMo?.disconnect();
      clearTimeout(this._timeout);
      this._settle();
    }

    _settle() {
      if (!this._pending) return;
      this._pending = false;
      pendingCount--;
      if (pendingCount <= 0) document.documentElement.classList.remove(PENDING_CLASS);
    }

    /* ---- Detection ---- */

    _waitForKaching() {
      if (this._tryBoot()) return;

      log('waiting for kaching widget…');
      let queued = false;
      this._bodyMo = new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          if (this._tryBoot()) this._bodyMo?.disconnect();
        });
      });
      this._bodyMo.observe(document.body, { childList: true, subtree: true, characterData: true });

      this._timeout = setTimeout(() => {
        this._bodyMo?.disconnect();
        log('timeout – no usable kaching widget found');
        this._fail();
      }, TIMEOUT_MS);
    }

    _tryBoot() {
      if (this._kaching) return true;
      const el = this._detect();
      if (!el) return false;
      clearTimeout(this._timeout);
      log('kaching widget found:', el);
      this._kaching = el;
      el.setAttribute('data-nd-bp-claimed', '');
      this._claimed = el;
      this._start();
      return true;
    }

    // Prefer a Kaching widget in our own section so multiple pickers on one page don't share one.
    _detect() {
      const section = this.closest('.shopify-section');
      const scopes = section ? [section, document] : [document];
      for (const scope of scopes) {
        for (const sel of KACHING_SELECTORS) {
          let nodes;
          try { nodes = scope.querySelectorAll(sel); } catch (_) { continue; }
          for (const el of nodes) {
            if (SKIP_TAGS.has(el.tagName) || this.contains(el) || el.contains(this)) continue;
            if (el.closest('[data-nd-bp-claimed]') && el.closest('[data-nd-bp-claimed]') !== this._claimed) continue;
            const elSection = el.closest('.shopify-section');
            if (elSection && elSection !== section && elSection.querySelector('nd-bundle-picker')) continue;
            const offers = this._findOfferEls(el);
            if (offers.length >= 2) {
              log('detect hit:', sel, 'offers:', offers.length);
              return el;
            }
          }
        }
      }
      return null;
    }

    _fail() {
      this._claimed?.removeAttribute('data-nd-bp-claimed');
      this._showKaching();
      this._settle();
      this.innerHTML = '';
      this.style.display = 'none';
    }

    /* ---- Lifecycle ---- */

    _start() {
      const snap = this._snapshot();
      log('parsed offers:', snap);
      if (snap.length < 2) { this._fail(); return; }

      this._hideKaching();
      this._debugStructure();
      this._snapJson = JSON.stringify(snap);
      this._render(snap);
      this._settle();
      this._observeKaching();
    }

    _observeKaching() {
      let queued = false;
      const schedule = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; this._refresh(); });
      };
      this._syncMo = new MutationObserver(schedule);
      this._syncMo.observe(this._kaching, { childList: true, subtree: true, characterData: true, attributes: true });
      ['change', 'click', 'deal-bar-selected', 'variant-selected', 'variants-changed'].forEach(type =>
        this._kaching.addEventListener(type, schedule, true));
    }

    _refresh() {
      const snap = this._snapshot();
      if (snap.length < 2) return;
      const json = JSON.stringify(snap);
      if (json === this._snapJson) return;
      log('kaching changed – re-rendering', snap);
      this._snapJson = json;
      this._render(snap);
    }

    _liveOfferEls() {
      if (!this._kaching.isConnected) {
        log('kaching widget was replaced – re-detecting');
        this._syncMo?.disconnect();
        this._kaching = null;
        const el = this._detect();
        if (!el) return [];
        this._kaching = el;
        el.setAttribute('data-nd-bp-claimed', '');
        this._claimed = el;
        this._hideKaching();
        this._observeKaching();
      }
      return this._findOfferEls(this._kaching);
    }

    /* ---- Hide / Show Kaching ---- */

    _hideKaching() {
      if (SHOW_SOURCE) {
        this._kachingOuter = null;
        return;
      }
      let el = this._kaching;
      const boundary = el.closest('.product__info-container, .product__info-wrapper') || document.body;
      while (
        el.parentElement &&
        el.parentElement !== boundary &&
        el.parentElement.children.length <= 2 &&
        !el.parentElement.contains(this)
      ) {
        el = el.parentElement;
      }
      this._kachingOuter = el;
      el.setAttribute('aria-hidden', 'true');
      el.classList.add('nd-bp-hidden-source');
    }

    _showKaching() {
      if (!this._kachingOuter) return;
      this._kachingOuter.removeAttribute('aria-hidden');
      this._kachingOuter.classList.remove('nd-bp-hidden-source');
    }

    /* ---- Product data ---- */

    _loadProductData() {
      try {
        const s = this.querySelector('script.nd-bp-data');
        this._product = s ? JSON.parse(s.textContent) : { variants: [] };
      } catch (_) {
        this._product = { variants: [] };
      }
    }

    /* ---- Parsing ---- */

    _text(el) {
      const parts = [];
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
        acceptNode: n => n.parentElement && n.parentElement.closest('script, style, template, noscript, option, select')
          ? NodeFilter.FILTER_REJECT
          : NodeFilter.FILTER_ACCEPT,
      });
      let n;
      while ((n = walker.nextNode())) {
        const t = n.textContent.replace(/\s+/g, ' ').trim();
        if (t) parts.push(t);
      }
      return parts.join(SEP);
    }

    _findOfferEls(root) {
      const withPrice = els => els.filter(e => PRICE_TEST.test(this._text(e)));

      const byTitle = withPrice(this._split(root, el => (this._text(el).match(TITLE_RE_G) || []).length));
      if (byTitle.length >= 2 && byTitle.length <= MAX_OFFERS) return byTitle;

      const byRadio = withPrice(this._split(root, el => el.querySelectorAll('input[type="radio"]').length));
      if (byRadio.length >= 2 && byRadio.length <= MAX_OFFERS) return byRadio;

      return [];
    }

    // Returns the outermost descendants that each contain exactly one match.
    _split(root, count) {
      const out = [];
      const visit = el => {
        for (const c of el.children) {
          if (SKIP_TAGS.has(c.tagName)) continue;
          const n = count(c);
          if (n === 1) out.push(c);
          else if (n > 1) visit(c);
        }
      };
      if (count(root) >= 2) visit(root);
      return out;
    }

    _prices(text) {
      return (text.match(PRICE_RE_G) || []).map(p => p.replace(/\s+/g, ' ').trim());
    }

    _num(price) {
      return parseInt(price.replace(/\D/g, ''), 10) || 0;
    }

    _snapshot() {
      const els = this._liveOfferEls();
      const offers = els.map((el, i) => this._parse(el, i));
      if (offers.length && !offers.some(o => o.selected)) {
        els.forEach((el, i) => {
          offers[i].selected = !!el.querySelector('[class*="--selected"], [class*="is-selected"], [class*="--active"], [aria-checked="true"]');
        });
      }
      // Kaching may render the colour rows outside the offer elements; give them to the selected offer.
      const sel = offers.findIndex(o => o.selected);
      this._looseOffer = sel;
      if (sel !== -1 && !offers[sel].variants.length) {
        offers[sel].variants = this._looseComboRows(els).map(row => ({
          value: row.value,
          options: this._colorOptions(row.value).map(name => ({ value: name, label: name, disabled: false })),
          img: '',
        }));
      }
      return offers;
    }

    _looseComboRows(els) {
      if (!this._kaching) return [];
      return this._comboRows(this._kaching).filter(r => !els.some(e => e.contains(r.cb)));
    }

    _offerComboRows(oi) {
      const els = this._liveOfferEls();
      const el = els[oi];
      if (!el) return [];
      const own = this._comboRows(el);
      if (own.length) return own;
      const radio = el.querySelector('input[type="radio"]');
      const isSelected = radio ? radio.checked : oi === this._looseOffer;
      return isSelected ? this._looseComboRows(els) : [];
    }

    _debugStructure() {
      if (!DEBUG || !this._kaching) return;
      const els = this._liveOfferEls();
      const all = this._comboRows(this._kaching);
      log('structure', {
        offers: els.map((e, i) => ({ index: i, selects: e.querySelectorAll('select').length, ownCombos: this._comboRows(e).length })),
        allCombos: all.length,
        looseCombos: this._looseComboRows(els).length,
        firstComboPath: all[0] ? this._path(all[0].cb) : null,
      });
    }

    _path(node) {
      const parts = [];
      for (let n = node; n && n !== this._kaching.parentElement; n = n.parentElement) {
        parts.unshift(n.tagName.toLowerCase() + (n.className && typeof n.className === 'string' ? '.' + n.className.trim().split(/\s+/).join('.') : ''));
      }
      return parts.join(' > ');
    }

    _parse(el, idx) {
      const text = this._text(el);
      const parts = text.split(SEP);

      const gIdx = text.search(/produktgaranti/i);
      const hasGuarantee = gIdx !== -1;
      let prices = this._prices(hasGuarantee ? text.slice(0, gIdx) : text);
      if (!prices.length) prices = this._prices(text);

      let price = prices[0] || '';
      let compare = prices[1] || '';
      if (price && compare && this._num(compare) < this._num(price)) [price, compare] = [compare, price];
      if (compare && this._num(compare) === this._num(price)) compare = '';

      const guaranteePrice = hasGuarantee ? (this._prices(text.slice(gIdx))[0] || '') : '';

      const titleM = text.match(TITLE_RE);
      const title = titleM
        ? titleM[0]
        : (parts.find(p => p.length > 2 && !PRICE_TEST.test(p) && !BADGE_RE.test(p)) || `Erbjudande ${idx + 1}`);

      const subM = text.match(/\d+\s+produkter?\s+til[l]?\s+pris(?:et|en)\s+(?:av|for)\s+[^|]+/i);
      const subtitle = subM ? subM[0].trim() : '';

      const badge = parts.find(p => BADGE_RE.test(p) && p.length < 40) || '';
      const badgeType = /popul/i.test(badge) ? 'popular' : 'value';

      const radio = el.querySelector('input[type="radio"]');
      const cls = el.getAttribute('class') || '';
      const selected = radio
        ? radio.checked
        : /selected|active|checked/i.test(cls) || el.getAttribute('aria-checked') === 'true' || el.getAttribute('aria-selected') === 'true';

      let variants = Array.from(el.querySelectorAll('select')).map(s => ({
        value: s.value,
        options: Array.from(s.options).map(o => ({ value: o.value, label: o.textContent.trim(), disabled: o.disabled })),
        img: this._rowImage(s, el),
      }));
      if (!variants.length) {
        variants = this._comboRows(el).map(row => ({
          value: row.value,
          options: this._colorOptions(row.value).map(name => ({ value: name, label: name, disabled: false })),
          img: '',
        }));
      }

      return { title, subtitle, price, compare, badge, badgeType, selected, hasGuarantee, guaranteePrice, variants };
    }

    _rowImage(select, stop) {
      let p = select.parentElement;
      while (p && p !== stop) {
        const img = p.querySelector('img');
        if (img) return img.currentSrc || img.src || '';
        p = p.parentElement;
      }
      return '';
    }

    /* ---- Rendering ---- */

    _render(offers) {
      const focused = this.contains(document.activeElement) ? this._focusKey(document.activeElement) : null;
      const heading = this.dataset.heading || '';
      const anySelected = offers.some(o => o.selected);
      const guaranteeText = this.dataset.guaranteeText || '+ GRATIS 1-års produktgaranti';
      const guaranteeMedia = this.dataset.guaranteeImage
        ? `<img src="${this._e(this.dataset.guaranteeImage)}" alt="" width="40" height="40" loading="lazy">`
        : `<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="M12 2.5 4.5 5.3v5.9c0 4.6 3.1 8.7 7.5 10.3 4.4-1.6 7.5-5.7 7.5-10.3V5.3L12 2.5Z" fill="currentColor"/><path d="m8.6 12.1 2.4 2.4 4.4-4.6" fill="none" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

      const cards = offers.map((o, i) => {
        const tabbable = o.selected || (!anySelected && i === 0);
        const badge = o.badge
          ? `<span class="nd-bp__badge nd-bp__badge--${o.badgeType}">${this._e(o.badge)}</span>` : '';

        let variants = '';
        if (o.variants.length) {
          const rows = o.variants.map((v, vi) => {
            const img = v.img || this._varImg(v.value);
            const opts = v.options.map(op =>
              `<option value="${this._e(op.value)}"${op.value === v.value ? ' selected' : ''}${op.disabled ? ' disabled' : ''}>${this._e(op.label)}</option>`
            ).join('');
            return `<div class="nd-bp__variant-row">
              ${img ? `<img class="nd-bp__variant-thumb" src="${this._e(img)}" alt="" loading="lazy" width="32" height="32">` : ''}
              <select class="nd-bp__variant-select" data-oidx="${i}" data-vidx="${vi}" aria-label="${({da: 'Farve', nb: 'Farge', no: 'Farge'})[(document.documentElement.lang || '').split('-')[0]] || 'Färg'} ${vi + 1}">${opts}</select>
            </div>`;
          }).join('');
          variants = `<div class="nd-bp__variants${o.selected ? '' : ' hidden'}">
            <p class="nd-bp__variants-label">${({da: 'Farve', nb: 'Farge', no: 'Farge'})[(document.documentElement.lang || '').split('-')[0]] || 'Färg'}</p>${rows}</div>`;
        }

        let guarantee = '';
        if (o.hasGuarantee) {
          const gp = o.guaranteePrice ? `<span class="nd-bp__guarantee-price">${this._e(o.guaranteePrice)}</span>` : '';
          guarantee = `<div class="nd-bp__guarantee">
            <span class="nd-bp__guarantee-media">${guaranteeMedia}</span>
            <span class="nd-bp__guarantee-text">${this._e(guaranteeText)}</span>${gp}
          </div>`;
        }

        const compare = o.compare ? `<span class="nd-bp__compare">${this._e(o.compare)}</span>` : '';

        return `<div class="nd-bp__offer${o.selected ? ' nd-bp__offer--selected' : ''}" data-idx="${i}" role="radio" aria-checked="${o.selected}" tabindex="${tabbable ? 0 : -1}">
          <span class="nd-bp__radio" aria-hidden="true"></span>
          <div class="nd-bp__info">
            <p class="nd-bp__title">${this._e(o.title)}${badge}</p>
            ${o.subtitle ? `<span class="nd-bp__subtitle">${this._e(o.subtitle)}</span>` : ''}
          </div>
          <div class="nd-bp__prices">
            <span class="nd-bp__price">${this._e(o.price)}</span>${compare}
          </div>
          ${variants}${guarantee}
        </div>`;
      }).join('');

      this.innerHTML = `<div class="nd-bp">
        ${heading ? `<p class="nd-bp__heading">${this._e(heading)}</p>` : ''}
        <div class="nd-bp__offers" role="radiogroup"${heading ? ` aria-label="${this._e(heading)}"` : ''}>${cards}</div>
      </div>`;

      this._bind();
      if (focused) this.querySelector(focused)?.focus();
    }

    _focusKey(el) {
      if (el.matches('.nd-bp__variant-select')) return `select[data-oidx="${el.dataset.oidx}"][data-vidx="${el.dataset.vidx}"]`;
      const card = el.closest('.nd-bp__offer');
      return card ? `.nd-bp__offer[data-idx="${card.dataset.idx}"]` : null;
    }

    _bind() {
      const cards = Array.from(this.querySelectorAll('.nd-bp__offer'));
      cards.forEach((card, i) => {
        card.addEventListener('click', e => {
          if (e.target.closest('select, option')) return;
          this._selectOffer(i);
        });
        card.addEventListener('keydown', e => {
          if (e.target !== card) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._selectOffer(i);
          } else if (['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(e.key)) {
            e.preventDefault();
            const step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
            const next = (i + step + cards.length) % cards.length;
            this._selectOffer(next);
            this.querySelector(`.nd-bp__offer[data-idx="${next}"]`)?.focus();
          }
        });
      });

      this.querySelectorAll('.nd-bp__variant-select').forEach(sel => {
        sel.addEventListener('change', () => {
          this._changeVariant(parseInt(sel.dataset.oidx, 10), parseInt(sel.dataset.vidx, 10), sel.value);
          const thumb = sel.closest('.nd-bp__variant-row')?.querySelector('.nd-bp__variant-thumb');
          const src = this._varImg(sel.value);
          if (thumb && src) thumb.src = src;
        });
      });
    }

    /* ---- Interactions (forwarded to Kaching) ---- */

    _selectOffer(idx) {
      this.querySelectorAll('.nd-bp__offer').forEach((c, i) => {
        const on = i === idx;
        c.classList.toggle('nd-bp__offer--selected', on);
        c.setAttribute('aria-checked', String(on));
        c.tabIndex = on ? 0 : -1;
        c.querySelector('.nd-bp__variants')?.classList.toggle('hidden', !on);
      });

      const el = this._liveOfferEls()[idx];
      if (!el) return;
      log('select offer', idx, el);

      const radio = el.querySelector('input[type="radio"]');
      if (radio) {
        radio.click();
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        (el.querySelector('label') || el).click();
      }
      if (this._colorMemory?.length) this._enqueue(() => this._reapplyColors(idx));
    }

    _changeVariant(oi, vi, value) {
      const el = this._liveOfferEls()[oi];
      const select = el?.querySelectorAll('select')[vi];
      if (!select) {
        if (el && this._offerComboRows(oi)[vi]) this._enqueue(() => this._changeCombo(oi, vi, value));
        return;
      }
      log('variant change', oi, vi, value);
      select.value = value;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* ---- Kaching swatch dropdown adapter (span[role="combobox"] + listbox) ---- */

    _comboRows(offerEl) {
      return Array.from(offerEl.querySelectorAll('[role="combobox"]'))
        .filter(cb => !this.contains(cb))
        .map(cb => {
          const wrap = cb.closest('.kaching-bundles-option');
          const label = cb.querySelector('span:last-of-type')?.textContent || cb.textContent;
          return { cb, wrap, value: (wrap?.dataset.value || label || '').trim() };
        });
    }

    _colorOptions(current) {
      const names = [];
      (this._product?.variants || []).forEach(v => { if (v.option1 && !names.includes(v.option1)) names.push(v.option1); });
      if (current && !names.some(n => this._same(n, current))) names.unshift(current);
      return names;
    }

    _same(a, b) {
      return String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
    }

    _enqueue(task) {
      this._queue = (this._queue || Promise.resolve()).then(task).catch(err => log('colour task failed', err));
      return this._queue;
    }

    _waitFor(check, timeout) {
      return new Promise(resolve => {
        const start = Date.now();
        const tick = () => {
          const result = check();
          if (result) return resolve(result);
          if (Date.now() - start >= timeout) return resolve(null);
          setTimeout(tick, 30);
        };
        tick();
      });
    }

    _press(el) {
      const Pointer = typeof PointerEvent === 'function' ? PointerEvent : MouseEvent;
      ['pointerdown', 'mousedown', 'pointerup', 'mouseup'].forEach(type => {
        const Ctor = type.startsWith('pointer') ? Pointer : MouseEvent;
        el.dispatchEvent(new Ctor(type, { bubbles: true, cancelable: true, view: window }));
      });
      el.click();
    }

    _findListbox(cb) {
      const id = cb.getAttribute('aria-controls');
      const byId = id ? document.getElementById(id) : null;
      const candidates = byId ? [byId] : Array.from(document.querySelectorAll('[role="listbox"]')).filter(l => !this.contains(l));
      return candidates.find(l => l.querySelector('[role="option"], [data-value], li')) || null;
    }

    async _openCombo(cb) {
      const open = this._findListbox(cb);
      if (open) return open;
      const attempts = [
        () => cb.click(),
        () => this._press(cb),
        () => cb.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true })),
        () => cb.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true })),
      ];
      for (const attempt of attempts) {
        attempt();
        const listbox = await this._waitFor(() => this._findListbox(cb), 400);
        if (listbox) return listbox;
        if (cb.getAttribute('aria-expanded') === 'true') return this._waitFor(() => this._findListbox(cb), 800);
      }
      return null;
    }

    _findOption(listbox, value) {
      let options = Array.from(listbox.querySelectorAll('[role="option"]'));
      if (!options.length) options = Array.from(listbox.querySelectorAll('[data-value], li'));
      return options.find(o => [o.dataset.value, o.getAttribute('aria-label'), o.getAttribute('title'), o.textContent]
        .some(label => label && this._same(label.replace(/\s+/g, ' '), value))) || null;
    }

    _closeCombo(cb) {
      cb.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
      if (cb.isConnected && cb.getAttribute('aria-expanded') === 'true') cb.click();
    }

    // Returns true only once Kaching itself reports the new colour.
    async _setComboColor(oi, vi, value) {
      const row = () => this._offerComboRows(oi)[vi] || null;
      const current = row();
      if (!current) return false;
      if (this._same(current.value, value)) return true;
      document.documentElement.classList.add('nd-bp-working');
      try {
        return await this._pickComboOption(current, row, value);
      } finally {
        document.documentElement.classList.remove('nd-bp-working');
      }
    }

    async _pickComboOption(current, row, value) {
      const listbox = await this._openCombo(current.cb);
      const option = listbox && this._findOption(listbox, value);
      if (!option) {
        const id = current.cb.getAttribute('aria-controls');
        log('colour option not found', { value, ariaControls: id, idInDocument: !!(id && document.getElementById(id)), expanded: current.cb.getAttribute('aria-expanded'), listbox: listbox?.outerHTML.slice(0, 3000) || null, combo: current.cb.outerHTML });
        if (current.cb.isConnected) this._closeCombo(current.cb);
        return false;
      }
      this._press(option);
      return !!(await this._waitFor(() => {
        const updated = row();
        return updated && this._same(updated.value, value);
      }, 1500));
    }

    async _changeCombo(oi, vi, value) {
      const ok = await this._setComboColor(oi, vi, value);
      if (ok) {
        this._colorMemory = this._colorMemory || [];
        this._colorMemory[vi] = value;
      } else {
        console.warn(`[nd-bundle-picker] Kaching did not accept colour "${value}" for cap ${vi + 1}; showing Kaching's current colour.`);
      }
      this._snapJson = '';
      this._refresh();
    }

    async _reapplyColors(oi) {
      await this._waitFor(() => this._offerComboRows(oi).length, 1500);
      const rows = this._offerComboRows(oi);
      for (let vi = 0; vi < rows.length; vi++) {
        const wanted = this._colorMemory[vi];
        const live = this._offerComboRows(oi)[vi];
        if (wanted && live && !this._same(live.value, wanted)) await this._setComboColor(oi, vi, wanted);
      }
      this._snapJson = '';
      this._refresh();
    }

    /* ---- Helpers ---- */

    _varImg(optVal) {
      const vs = this._product?.variants;
      if (!vs?.length) return '';
      const v = vs.find(v => v.option1 === optVal || v.title === optVal || String(v.id) === String(optVal));
      return v?.image || '';
    }

    _e(s) {
      return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }
  }

  if (!customElements.get('nd-bundle-picker')) {
    customElements.define('nd-bundle-picker', NdBundlePicker);
  }
})();
